import {
  RSAARequestType,
  RSAASuccessType,
  RSAAFailureType,
  RSAAResultAction,
  RSAACall,
  createAction,
} from 'redux-api-middleware';
import { AppState } from '../redux/appState';
import { ActionType } from '../redux/actionType';
import { Action } from 'redux';
import * as R from 'ramda';


/**
 * Each RSAA Action results in 2 Redux actions being dispatched:
 * 1. The action at index 0 to indicate the HTTP request has been made and is in progress
 * 2. A result action to indicate success/failure of the HTTP request, including the server response
 *    Index 1 = Successful Response Action Type
 *    Index 2 = Unsuccessful Response Action Type
 */
export type RSAATypes<TPayload = any, TMeta = undefined> = [
  RSAARequestType<AppState, TPayload, TMeta>,
  RSAASuccessType<AppState, TPayload, TMeta>,
  RSAAFailureType<AppState, TPayload, TMeta>
]


/**
 * Internal type for a function that maps an ActionType into an RSAA Action Type
 */
type RsaaTypeMapper<TPayload, TMeta> = (action: ActionType) => RSAARequestType<AppState, TPayload, TMeta>;


/**
 * A function of the kind accepted by `makeApiReducer` that takes information about a reduced API action such as whether
 * or not there was an HTTP error, whether we're waiting on a response, what the current redux store looks like...
 */
export type ApiReducer<TPayload = any, TMeta = undefined> = (
  state: AppState,
  action: RSAAResultAction<TPayload, TMeta> & { meta: TMeta }, // RSAA typing missed this property for error actions
  errorMsg: string | undefined,
  isLoading: boolean,
  payload: TPayload | undefined,
) => AppState;


/**
 * A function that helps turn ActionTypes into RSAATypes, optionally with some meta.
 * @param actions - The Redux ActionTypes RSAA should dispatch to show the state of the HTTP request
 * @param meta - An optional object (or other thing) to pass along on each Redux action to the reducer
 *               (useful for tracking e.g. which product this request was made for)
 */
export const makeRsaaTypes = <TPayload = any, TMeta = undefined>(
  actions: ActionType[],
  meta?: TMeta,
): RSAATypes<TPayload, TMeta> => {
  // Map meta into an object, otherwise a good old fashioned string[] is fine
  const mapper: RsaaTypeMapper<TPayload, TMeta> = meta
    ? (action => ({ type: action, meta: meta }))
    : (action => action as string);

  // RSAATypes is an array of length 3, and map returns an array of length indefinite -- so we cast :(
  return actions.map(mapper) as RSAATypes<TPayload, TMeta>;
};


/**
 * Constructs an action that, when dispatched, RSAA Middleware intercepts and turns into an HTTP Request.
 */
export const makeApiAction = <TPayload = any, TMeta = undefined>(
  request: RSAACall<AppState, TPayload, TMeta>,
): Action => createAction<AppState, TPayload, TMeta>(
  request,
) as unknown as Action; // Redux needs this to be an Action even though it doesn't have a `type` attribute


/**
 * Constructs a reducer for handling actions from ReduxApiMiddleware.
 * @param actions - The list of actions you care about
 * @param loadingAction - The API Middleware's loading action
 * @param withResults - Your reducer logic
 * @param defaultErrorMsg - Sometimes the API returns no error message -- here's where you can use this!
 */
export const makeApiReducer = <TPayload = any, TMeta = undefined>(
  actions: ActionType[],
  loadingAction: ActionType,
  withResults: ApiReducer<TPayload, TMeta>,
  defaultErrorMsg: string = '',
) => (
  state: AppState,
  incomingAction: Action,
): AppState => {
  if (!actions.includes(incomingAction.type)) {
    return state;
  }

  const action  = incomingAction as RSAAResultAction<TPayload, TMeta>;
  const payload = R.path(['payload'], action) as TPayload;

  const errorMsg  = action.error ? R.path<string>(['message'], payload) ?? defaultErrorMsg : undefined;
  const isLoading = action.type === loadingAction && !errorMsg;

  return withResults(
    state,
    action as RSAAResultAction<TPayload, TMeta> & { meta: TMeta }, // RSAA typing missed this property for error actions
    errorMsg,
    isLoading,
    payload,
  );
};

